import React from 'react'
import { Link } from 'react-router-dom'
import SEO from '../SEO'
import { SplitSection, StackRibbon } from '../ServicePage'
import ContactForm from '../ContactForm'
import { trackCtaClick, trackOutboundClick } from '../../utils/analytics'
import './../../styles/CaseStudy.css'

/**
 * Inline Instant Offices mark — stylised office-window grid.
 * No real logo file in the asset library yet, so we draw a clean
 * placeholder that fits the editorial style.
 */
const InstantOfficesMark = ({ size = 64 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 120 120"
    xmlns="http://www.w3.org/2000/svg"
    aria-label="Instant Offices"
    role="img"
  >
    <defs>
      <linearGradient id="io-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0066ff" />
        <stop offset="100%" stopColor="#00d4ff" />
      </linearGradient>
    </defs>
    {/* Building outline */}
    <rect x="20" y="22" width="80" height="80" rx="8" fill="rgba(255,255,255,0.04)" stroke="url(#io-grad)" strokeWidth="2" />
    {/* Window grid */}
    {[0, 1, 2].map((row) =>
      [0, 1, 2].map((col) => (
        <rect
          key={`${row}-${col}`}
          x={32 + col * 18}
          y={34 + row * 18}
          width="12"
          height="12"
          rx="2"
          fill={(row + col) % 2 === 0 ? 'url(#io-grad)' : 'rgba(0,212,255,0.20)'}
        />
      )),
    )}
    {/* Door */}
    <rect x="52" y="88" width="16" height="14" rx="2" fill="rgba(0,212,255,0.55)" />
  </svg>
)

/* Hero visual — three connected building blocks (one per brand) over a global glow */
const InstantOfficesHero = () => (
  <svg viewBox="0 0 460 460" xmlns="http://www.w3.org/2000/svg" className="cs-hero__svg" aria-hidden="true">
    <defs>
      <linearGradient id="io-hero-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0066ff" />
        <stop offset="100%" stopColor="#00d4ff" />
      </linearGradient>
      <radialGradient id="io-glow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#00d4ff" stopOpacity="0.45" />
        <stop offset="100%" stopColor="#00d4ff" stopOpacity="0" />
      </radialGradient>
    </defs>

    <circle cx="230" cy="230" r="200" fill="url(#io-glow)">
      <animate attributeName="r" values="200;215;200" dur="6s" repeatCount="indefinite" />
    </circle>

    {/* three buildings — left, centre, right (one per brand) */}
    {/* left building */}
    <g transform="translate(70 200)">
      <rect width="80" height="160" rx="6" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.20)" />
      {[0,1,2,3].map((r) =>
        [0,1,2].map((c) => (
          <rect key={`L${r}${c}`} x={10 + c * 22} y={14 + r * 30} width="14" height="14" rx="2"
            fill={(r + c) % 2 === 0 ? 'rgba(0,212,255,0.55)' : 'rgba(0,102,255,0.45)'}>
            <animate attributeName="opacity" values="0.55;1;0.55" dur={`${3 + (r + c) * 0.3}s`} repeatCount="indefinite" />
          </rect>
        ))
      )}
    </g>

    {/* centre building (taller — flagship) */}
    <g transform="translate(180 130)">
      <rect width="100" height="230" rx="8" fill="rgba(255,255,255,0.06)" stroke="url(#io-hero-grad)" strokeWidth="2" />
      {[0,1,2,3,4,5,6].map((r) =>
        [0,1,2,3].map((c) => (
          <rect key={`C${r}${c}`} x={10 + c * 22} y={14 + r * 30} width="14" height="14" rx="2"
            fill={(r + c) % 2 === 0 ? 'url(#io-hero-grad)' : 'rgba(0,212,255,0.30)'}>
            <animate attributeName="opacity" values="0.6;1;0.6" dur={`${3 + (r + c) * 0.25}s`} repeatCount="indefinite" />
          </rect>
        ))
      )}
      {/* roof beacon */}
      <circle cx="50" cy="-6" r="4" fill="#00d4ff">
        <animate attributeName="opacity" values="0.4;1;0.4" dur="1.6s" repeatCount="indefinite" />
      </circle>
    </g>

    {/* right building */}
    <g transform="translate(310 220)">
      <rect width="80" height="140" rx="6" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.20)" />
      {[0,1,2,3].map((r) =>
        [0,1,2].map((c) => (
          <rect key={`R${r}${c}`} x={10 + c * 22} y={14 + r * 26} width="14" height="14" rx="2"
            fill={(r + c) % 2 === 0 ? 'rgba(0,102,255,0.55)' : 'rgba(0,212,255,0.40)'}>
            <animate attributeName="opacity" values="0.55;1;0.55" dur={`${3 + (r + c) * 0.3}s`} repeatCount="indefinite" />
          </rect>
        ))
      )}
    </g>

    {/* connecting flow lines */}
    <g stroke="rgba(0,212,255,0.35)" strokeDasharray="3 6" fill="none">
      <path d="M150 280 Q190 250 230 270" />
      <path d="M280 270 Q320 250 360 280" />
    </g>

    {/* AWS cloud icon (top-right) */}
    <g transform="translate(380 90)">
      <circle r="22" fill="rgba(255,255,255,0.06)" stroke="rgba(0,212,255,0.55)" />
      <path d="M-10 4 a6 6 0 1 1 4 -10 a8 8 0 0 1 14 4 a5 5 0 0 1 -2 10 z" fill="rgba(0,212,255,0.85)" />
    </g>
  </svg>
)

const pressLinks = [
  {
    href: 'https://www.instantoffices.com/',
    src: 'Instant Offices',
    title: 'Global flexible workspace platform — official site',
  },
  {
    href: 'https://www.coworker.com/',
    src: 'Coworker',
    title: 'Coworking spaces directory — official site',
  },
  {
    href: 'https://www.easyoffices.com/',
    src: 'Easy Offices',
    title: 'Office space search — official site',
  },
]

const InstantOffices = () => (
  <>
    <SEO
      path="/case-studies/instant-offices"
      title="Instant Offices — 3-brand QA, Playwright Automation & Cloud Migration"
      description="QA Tech rebuilt the entire QA and test automation programme for Instant Offices, coworker.com and easyoffices — three brands, one scalable Playwright + TypeScript framework, plus support on a Rackspace → AWS migration with disaster recovery."
      keywords="Instant Offices case study, coworker.com QA, easyoffices QA, Playwright automation, TypeScript test framework, multi-brand QA strategy, AWS migration, disaster recovery testing, CMS migration, CI/CD test integration, PHP Laravel React testing"
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline:
          'Instant Offices — three-brand QA programme, Playwright automation and cloud migration',
        author: { '@type': 'Organization', name: 'QA Tech' },
        publisher: { '@type': 'Organization', name: 'QA Tech', url: 'https://qatechltd.com' },
        about: ['Instant Offices', 'coworker.com', 'easyoffices', 'Playwright', 'AWS migration', 'Multi-brand QA'],
        url: 'https://qatechltd.com/case-studies/instant-offices',
      }}
    />

    <div className="body cs-body">
      <header className="cs-hero">
        <div className="cs-hero__bg" aria-hidden="true">
          <span className="cs-hero__orb cs-hero__orb--a" />
          <span className="cs-hero__orb cs-hero__orb--b" />
          <span className="cs-hero__grid" />
        </div>

        <div className="cs-hero__inner">
          <nav className="sp-breadcrumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link><span aria-hidden="true">/</span>
            <Link to="/case-studies">Case Studies</Link><span aria-hidden="true">/</span>
            <span>Instant Offices</span>
          </nav>

          <div className="cs-hero__content">
            <div className="cs-hero__client">
              <InstantOfficesMark size={56} />
              <div className="cs-hero__client-text">
                <span className="eyebrow">Case study</span>
                <h2 className="cs-hero__client-name">Instant Offices</h2>
                <p className="cs-hero__client-sub">Global flexible-workspace group &mdash; <strong>Instant Offices</strong>, <strong>coworker.com</strong>, <strong>easyoffices</strong></p>
              </div>
            </div>

            <h1 className="cs-hero__title">
              Three brands. One QA strategy. <span className="cs-hero__title-accent">Cloud-migrated, end-to-end.</span>
            </h1>

            <p className="cs-hero__lede">
              We rebuilt the entire QA and test automation programme for
              one of the world&rsquo;s largest flexible-workspace groups
              &mdash; covering <strong>Instant Offices</strong>,{' '}
              <strong>coworker.com</strong> and <strong>easyoffices</strong>.
              One scalable, reusable Playwright + TypeScript framework
              across all three brands, full CI/CD test integration on
              every build, and engineering support across a major cloud
              migration with disaster recovery.
            </p>

            <div className="cs-hero__cta-row">
              <a
                href="#cs-contact"
                className="sp-cta-primary"
                onClick={() => trackCtaClick('Work with us', 'case_study_instantoffices_hero')}
              >
                Work with us
              </a>
              <a href="#cs-overview" className="sp-cta-secondary">Read the case study &darr;</a>
            </div>
          </div>

          <div className="cs-hero__visual" aria-hidden="true">
            <InstantOfficesHero />
          </div>
        </div>
      </header>

      <section className="cs-stats" aria-label="Project at a glance" id="cs-overview">
        <div className="cs-stats__inner">
          <div className="cs-stat"><span className="cs-stat__num">3 brands</span><span className="cs-stat__label">Instant Offices, coworker.com, easyoffices</span></div>
          <div className="cs-stat"><span className="cs-stat__num">200+</span><span className="cs-stat__label">end-to-end tests per brand</span></div>
          <div className="cs-stat"><span className="cs-stat__num">23 months</span><span className="cs-stat__label">engagement (2024 &ndash; 2026)</span></div>
          <div className="cs-stat"><span className="cs-stat__num">Rackspace → AWS</span><span className="cs-stat__label">cloud migration with disaster recovery</span></div>
        </div>
      </section>

      <main className="cs-main">
        <SplitSection index={1} align="left" kicker="The brief" title="A global workspace platform with three brands and no real automation.">
          <p>
            Instant Offices powers the search, listing and booking of
            flexible workspace at a global scale &mdash; through three
            consumer-facing brands: <strong>Instant Offices</strong>,{' '}
            <strong>coworker.com</strong> and <strong>easyoffices</strong>.
            Three different domains, three different audiences, one
            shared engineering platform. They needed a QA programme that
            could keep up &mdash; and an infrastructure migration off
            ageing servers without a regression nightmare.
          </p>
        </SplitSection>

        <SplitSection index={2} align="right" kicker="Test strategy" title="One framework. Three brands. Reusable everywhere.">
          <p>
            We built a robust, scalable and highly{' '}
            <strong>reusable</strong> test automation strategy that spans
            all three brands inside the group. The same framework, the
            same patterns, the same conventions &mdash; instead of three
            disconnected suites that age separately. Reuse is what made
            it sustainable: when a flow is shared across brands, it&rsquo;s
            tested once and trusted everywhere.
          </p>
          <ul>
            <li>Single Playwright + TypeScript framework across all three brands</li>
            <li>Shared page objects, fixtures and utilities</li>
            <li>200+ end-to-end tests per project / per brand</li>
            <li>Auditing &amp; QA gates at every point in the delivery pipeline</li>
          </ul>
        </SplitSection>

        <SplitSection index={3} align="left" kicker="Automation" title="Playwright — UI and API, in the same framework.">
          <p>
            We built out the company&rsquo;s automation capability from
            scratch using <strong>Playwright</strong> and{' '}
            <strong>TypeScript</strong>. One framework covers both the
            front-end UI flows and the backend API contracts &mdash; no
            second tool to maintain, no second context to keep in your
            head. Tests are stable, parallel-safe and engineered for
            reuse across the three brands.
          </p>
          <ul>
            <li>UI end-to-end automation with Playwright</li>
            <li>Backend / API automation in the same framework</li>
            <li>Stable selectors and page-object architecture</li>
            <li>Tests treated like product code: reviewed, typed, peer-merged</li>
          </ul>
        </SplitSection>

        <SplitSection index={4} align="right" kicker="CI/CD" title="From PR build to production smoke — fully automated.">
          <p>
            Every project &mdash; <strong>Instant Offices</strong>,{' '}
            <strong>coworker.com</strong>, <strong>easyoffices</strong>{' '}
            &mdash; runs the relevant tests on every pull request and
            every staging deployment. Once a release ships to production,
            an automated <strong>smoke suite</strong> fires against the
            live site to confirm the critical paths are still green.
            Manual exploratory testing fills the gaps automation will
            never catch.
          </p>
          <ul>
            <li>PR builds run the relevant suite before merge</li>
            <li>Staging deployments trigger full regression</li>
            <li>Production smoke tests run automatically post-release</li>
            <li>Manual exploratory testing alongside the automation</li>
          </ul>
        </SplitSection>

        <SplitSection index={5} align="left" kicker="Cloud migration" title="Rackspace → AWS, including disaster recovery.">
          <p>
            We supported a major infrastructure migration off ageing
            <strong> Rackspace</strong> servers onto <strong>AWS</strong>,
            including <strong>disaster recovery</strong> setup. The CI/CD
            tooling moved alongside &mdash; from{' '}
            <strong>Azure / TeamCity</strong> onto AWS-native pipelines.
            Test coverage was the safety net throughout: every regression
            risk in the migration was something the automated suite was
            already watching.
          </p>
          <ul>
            <li>Rackspace → AWS server migration</li>
            <li>Disaster recovery configured and validated</li>
            <li>CI/CD migrated from Azure / TeamCity to AWS</li>
            <li>Migration validated with the same regression suite that runs every PR</li>
          </ul>
        </SplitSection>

        <SplitSection index={6} align="right" kicker="CMS &amp; CRM" title="Migrated, supported, tested.">
          <p>
            We supported the CMS migration that ran in parallel with the
            cloud move, and provided testing support for the internal
            <strong> CRM system</strong> that powers the workspace
            operators side of the business. SEO support went hand-in-hand
            with the CMS work &mdash; making sure the migration
            didn&rsquo;t cost the brands hard-won search rankings.
          </p>
        </SplitSection>

        <SplitSection index={7} align="left" kicker="Stack" title="PHP Laravel on the back. React on the front.">
          <p>
            The engineering platform behind the brands runs on{' '}
            <strong>PHP / Laravel</strong> with a <strong>React</strong>{' '}
            front end. We worked the seams between the two &mdash;
            validating contracts, catching regressions, and contributing
            to testability conventions on both sides.
          </p>
        </SplitSection>

        <SplitSection index={8} align="right" kicker="Ways of working" title="Stakeholders, developers, accessibility — all in the room.">
          <p>
            We worked agile (Scrum) with stakeholders to define
            requirements, testability, user experience and accessibility
            up front &mdash; not after. We worked alongside developers to
            improve performance, raise the testability bar and feed
            real-user observations back into the roadmap. Defects were
            triaged and prioritised in Jira on a tight cadence so nothing
            got buried.
          </p>
        </SplitSection>

        <StackRibbon
          label="What we delivered"
          items={[
            'Test strategy from scratch',
            'Scalable, reusable, multi-brand test architecture',
            'Playwright UI + API automation',
            'Full CI/CD test integration',
            'Production smoke testing',
            'Manual exploratory testing',
            'Cloud migration support (Rackspace → AWS)',
            'Disaster recovery validation',
            'CI/CD migration (Azure / TeamCity → AWS)',
            'CMS migration support',
            'CRM system testing support',
            'SEO support during migration',
            'Stakeholder &amp; accessibility partnership',
            'Defect triage &amp; prioritisation',
          ]}
        />

        <StackRibbon
          label="Tech &amp; tools"
          items={[
            'Playwright', 'TypeScript', 'JavaScript',
            'PHP', 'Laravel', 'React',
            'AWS', 'Rackspace (legacy)',
            'Azure (legacy)', 'TeamCity (legacy)',
            'Jira', 'Agile (Scrum)',
          ]}
        />

        <section className="cs-press" aria-label="Brands and properties">
          <div className="cs-press__inner">
            <span className="sp-stack__label">The three brands</span>
            <p className="cs-press__lead">
              Each of the brands we delivered the QA programme across.
            </p>
            <ul className="cs-press__list">
              {pressLinks.map((p) => (
                <li key={p.href}>
                  <a href={p.href} target="_blank" rel="noopener noreferrer"
                    onClick={() => trackOutboundClick(p.href, p.title, 'case_study_instantoffices_brands')}>
                    <span className="cs-press__src">{p.src}</span>
                    <span className="cs-press__title">{p.title}</span>
                  </a>
                </li>
              ))}
            </ul>
            <p className="cs-press__note">
              External links open in a new tab. QA Tech is independent and
              not affiliated with The Instant Group, Instant Offices,
              coworker.com or easyoffices.
            </p>
          </div>
        </section>
      </main>

      <section className="sp-cta-section">
        <div className="sp-cta-section__inner">
          <h2>Multi-brand programme on the horizon?</h2>
          <p>If you run more than one brand on a shared engineering platform, we&rsquo;ve done it &mdash; properly.</p>
        </div>
      </section>

      <div id="cs-contact"><ContactForm /></div>
    </div>
  </>
)

export default InstantOffices
